/**
 * CreateClubPage - Club Creation Form
 * 
 * Single-page form for creating a new club.
 * Rebuilt to match Figma Nightfall Kit design with clean layout.
 * 
 * Reference: docs/design/FIGMA_IMPLEMENTATION_PLAN.md - Phase 7C
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateClub } from '../hooks/useClubs';
import type { ApiError } from '../lib/api';
import { TopBar, Button, Input, HeroImageUpload } from '../components';

export default function CreateClubPage() {
  const navigate = useNavigate();
  const createClub = useCreateClub();
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedHeroImage, setSelectedHeroImage] = useState<File | null>(null);
  const [heroImageUrl, setHeroImageUrl] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Client-side validation
    if (!name.trim()) {
      setValidationError('Club name is required');
      return;
    }

    if (name.trim().length < 3) {
      setValidationError('Club name should be at least 3 characters');
      return;
    }

    if (name.trim().length > 100) {
      setValidationError('Club name should be less than 100 characters');
      return;
    }

    setValidationError(null);

    // Create club using mutation
    createClub.mutate(
      {
        name: name.trim(),
        description: description.trim() || undefined,
        city: 'TBD',
        state: 'TBD',
        cover_image_url: undefined, // Hero image will be uploaded after club creation
      },
      {
        onSuccess: async (response) => {
          const clubId = response.club.id;
          
          // Upload hero image after club creation if selected
          if (selectedHeroImage && clubId) {
            try {
              const { clubsApi } = await import('../lib/api/clubs');
              await clubsApi.uploadClubHeroImage(clubId, selectedHeroImage);
            } catch (error) {
              console.warn('Failed to upload club hero image:', error);
              // Still navigate even if image upload fails
            }
          }
          
          navigate(`/clubs/${response.club.slug}`);
        },
      }
    );
  };

  return (
    <div className="flex flex-col bg-oro-bg">
      <TopBar 
        title="Create Club" 
        onBack={() => navigate('/clubs')}
      />
      
      <div className="flex-1 overflow-y-auto">
        <form onSubmit={handleSubmit} className="px-4 py-6 space-y-6">
          {/* Hero Image Upload */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-text-secondary">
              Club Cover Photo
            </label>
            <HeroImageUpload
              currentImageUrl={heroImageUrl}
              onImageChange={setHeroImageUrl}
              onImageUpload={async (file) => {
                // Store file for upload after club creation
                setSelectedHeroImage(file);
                
                // Create preview URL for immediate UI feedback
                const previewUrl = URL.createObjectURL(file);
                setHeroImageUrl(previewUrl);
                
                return previewUrl;
              }}
              onImageRemove={async () => {
                setSelectedHeroImage(null);
                setHeroImageUrl(null);
                
                // Clean up preview URL
                if (heroImageUrl && heroImageUrl.startsWith('blob:')) {
                  URL.revokeObjectURL(heroImageUrl);
                }
              }}
              enableCrop={true}
              className="w-full"
              disabled={createClub.isPending}
            />
            <p className="text-xs text-text-muted">
              Optional: Upload a cover photo for your club
            </p>
          </div>

          {/* Name Input */}
          <div className="space-y-2">
            <label
              htmlFor="name"
              className="block text-sm font-medium text-text-secondary"
            >
              Club Name <span className="text-accent">*</span>
            </label>
            <Input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={createClub.isPending}
              placeholder="Jewel City Run Club"
              required
              maxLength={100}
              size="lg"
            />
            <p className="text-xs text-text-muted">
              Choose something memorable and unique
            </p>
          </div>

          {/* Description Textarea */}
          <div className="space-y-2">
            <label
              htmlFor="description"
              className="block text-sm font-medium text-text-secondary"
            >
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={createClub.isPending}
              rows={4}
              className="w-full px-4 py-3 rounded-lg border border-oro-border bg-oro-surface text-text-primary placeholder:text-text-muted focus:border-accent focus:ring-2 focus:ring-accent/20 disabled:opacity-50 disabled:cursor-not-allowed resize-none transition-colors"
              placeholder="We run. We talk. We drink coffee."
              maxLength={500}
            />
            <p className="text-xs text-text-muted">
              Tell people what your club is about (optional)
            </p>
          </div>

          {/* Submit Buttons */}
          <div className="flex flex-col gap-3 pt-4">
            {/* Error Message - shown near submit button for better UX */}
            {(validationError || createClub.error) && (
              <div className="rounded-lg border border-oro-error-border bg-oro-error-soft p-4">
                <p className="text-sm text-oro-error">
                  {validationError || ((createClub.error as unknown as ApiError)?.message || 'Failed to create club. Please try again.')}
                </p>
              </div>
            )}

            <Button
              type="submit"
              disabled={createClub.isPending}
              variant="primary"
              loading={createClub.isPending}
              size="lg"
              className="w-full"
            >
              Create Club
            </Button>
            <Button
              type="button"
              onClick={() => navigate('/clubs')}
              disabled={createClub.isPending}
              variant="ghost"
              size="lg"
              className="w-full"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
